import { Box } from '@mui/material';
import { PersistedFixedSizeList as FixedSizeList } from "../../../../hooks/listNavigation";
import { useNavigate } from 'react-router-dom';
import CustomHeaderCell from '../../../../utils/customHeaderCell';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import MachineContractListViewData from './machineContractListViewData';
import { useScrollFetchData } from '../../../../hooks/useScrollFetchData';
import { useCalculateFetchedItems } from '../../../../hooks/useCalculateFetchItems';
import { useSyncHorizontalScroll } from '../../../../hooks/useSyncHorizontalScroll';
import { debounce } from 'lodash';

const MachineContractListView = React.memo(({ companies, contractFilters, setContractFilters, sortBy, setSortBy, setOffset, scrollRef, contracts, filteredRecordCount, setFetchedContracts }) => {

    const navigate = useNavigate();

    const defaultColumnWidth = {
        title: 250,
        company: 250,
        contract_number: 250,
        date: 250,
        auto_renew: 250,
        machines: 250,
    };

    const [machineContractColumnWidth, setMachineContractColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('machineContractColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const auto_renewOptions = [{ id: 'true', name: 'Automatique' }, { id: 'false', name: 'Non automatique' }];

    const tableHeadRef = useRef(null);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredRecordCount, setFetchedContracts, 70, 430);

    // Handle scroll and fetch more items
    const handleScroll = useScrollFetchData(setOffset, setFetchedContracts, filteredRecordCount, 70, 430);

    const handleContractClick = useCallback((id) => {
        navigate(`/dashboard/contract/contract_details/${id}`)
    }, [navigate]);

    const handleColumnWidthChange = (value, columnKey) => {
        setMachineContractColumnWidth({ ...machineContractColumnWidth, [columnKey]: value })
    }

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    useEffect(() => {
        localStorage.setItem('machineContractColumnWidth', JSON.stringify(machineContractColumnWidth));
    }, [machineContractColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { contracts, handleContractClick } = data;
        const contract = index === 0 ? contracts[0] : contracts[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <MachineContractListViewData
                    contract={contract}
                    columnWidth={machineContractColumnWidth}
                    handleContractClick={handleContractClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    label='Titre'
                    columnKey='title'
                    isFixed={true}
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    backgroundColor='#F6F6F6'
                    columnWidth={machineContractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    label='Entreprise'
                    columnKey='company'
                    backgroundColor='#F6F6F6'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={true}
                    value={contractFilters.company}
                    onChange={(e) => setContractFilters({ ...contractFilters, company: e.target.value })}
                    options={companies}
                    columnWidth={machineContractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    label='Numero du contrat'
                    columnKey='contract_number'
                    backgroundColor='#F6F6F6'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    columnWidth={machineContractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    label='Date'
                    columnKey='date'
                    backgroundColor='#F6F6F6'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={true}
                    value={contractFilters.date}
                    onChange={(e) => { setContractFilters({ ...contractFilters, date: e }) }}
                    options='date'
                    columnWidth={machineContractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    label='Renouvellement automatique'
                    columnKey='auto_renew'
                    backgroundColor='#F6F6F6'
                    isFilter={true}
                    value={contractFilters.auto_renew}
                    onChange={(e) => { setContractFilters({ ...contractFilters, auto_renew: e.target.value }) }}
                    options={auto_renewOptions}
                    columnWidth={machineContractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    label='Machines liées'
                    columnKey='machines'
                    backgroundColor='#F6F6F6'
                    columnWidth={machineContractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell backgroundColor='#F6F6F6' />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredRecordCount * 70 + 15,
                        windowHeight - 430
                    )}
                    width='100%'
                    itemCount={filteredRecordCount}
                    itemSize={70}
                    itemData={{ contracts, handleContractClick }}
                    onScroll={handleScroll}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>
    );
});

export default MachineContractListView;